import { compareAsc, compareDesc, isAfter, isBefore, parseISO } from "date-fns";
import type React from "react";
import { createContext, useContext, useState } from "react";
import type { SectionEventsItemSchema } from "~components/cms/section-events-item/types";
import { useFilterEvents } from "./filter-events-context-provider";

interface SectionEventsContextProps {
  currentEvents: SectionEventsItemSchema[];
  pastEvents: SectionEventsItemSchema[];
  sortOrder: "asc" | "desc";
  toggleSortOrder: () => void;
}

const OrderEventsContext = createContext<SectionEventsContextProps | undefined>(
  undefined,
);

interface OrderEventsProviderProps {
  children: React.ReactNode;
}

const getStart = (event: SectionEventsItemSchema) => parseISO(event.startDate);
const getEnd = (event: SectionEventsItemSchema) =>
  event.endDate ? parseISO(event.endDate) : parseISO(event.startDate);

export const OrderEventsProvider = ({ children }: OrderEventsProviderProps) => {
  const now = new Date();
  const { filteredEvents } = useFilterEvents();

  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const toggleSortOrder = () => {
    setSortOrder(prevOrder => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const sortedEvents = [...filteredEvents].sort((a, b) => {
    const comparisonFn = sortOrder === "asc" ? compareAsc : compareDesc;
    const startCmp = comparisonFn(getStart(a), getStart(b));
    if (startCmp !== 0) return startCmp;
    return comparisonFn(getEnd(a), getEnd(b));
  });

  const currentEvents = sortedEvents.filter(event => {
    const end = getEnd(event);
    return isAfter(end, now) || +end === +now;
  });

  const pastEvents = sortedEvents.filter(event => {
    const end = getEnd(event);
    return isBefore(end, now);
  });

  const value = {
    currentEvents,
    pastEvents,
    sortOrder,
    toggleSortOrder,
  };

  return (
    <OrderEventsContext.Provider value={value}>
      {children}
    </OrderEventsContext.Provider>
  );
};

export const useOrderEventsContext = () => {
  const context = useContext(OrderEventsContext);
  if (context === undefined) {
    throw new Error(
      "useOrderEventsContext must be used within a OrderEventsProvider",
    );
  }
  return context;
};
